/**
 * The retention ladder, mirroring the api's `com.transferify.business.ExpireAfter`.
 *
 * **The member NAMES are the wire contract; the numbers are not.** Every request and response now carries
 * `expireAfter` as the api's enum NAME (kitecorp/transferify-api-service#690) — creates included, which
 * were the last holdout — so a member's numeric value is an app-internal identity that nothing outside
 * this repo can observe. Renaming a member is therefore the breaking change here, and renumbering one is
 * not; that is the exact reverse of the rule this file carried until #690, so do not restore the old
 * "append only" reasoning from memory.
 *
 * The api's ordinals still matter on ITS side — `transfers.expire_after` is an integer column holding the
 * JPA ordinal mapping — which is why `ExpireAfterTest` keeps pinning them there while the pin HERE is
 * name-based. The order a picker SHOWS is `home/gating`'s `EXPIRY_PICKER_LADDER`, never this one, and the
 * declaration order below is kept matching the api's only so the two files stay readable side by side.
 *
 * `ExpiresAfter.test.ts` pins the name set here; the api pins the same names through its OpenAPI baseline
 * (`OpenApiDocsTest`'s ordered `containsExactly` over the emitted enum). Neither pin can see the other, by
 * design — and the generated `schema.d.ts` union now binds the two together at COMPILE time, because
 * `transferApi`'s wire types are checked against it (a numeric enum used to narrow the spec's `number`
 * and slip through, which is what made a silent renumber possible at all).
 */
enum ExpiresAfter {
	ONE_DAY, TWO_DAYS, THREE_DAYS, FOUR_DAYS, FIVE_DAYS, SIX_DAYS,
	SEVEN_DAYS, TWO_WEEKS, THREE_WEEKS, ONE_MONTH,
	NEVER,
	// Sub-day rungs (kitecorp/transferify#90) — listed here in the api's own declaration order, which puts
	// them after NEVER because ITS ordinals are persisted; see the contract note above.
	FIFTEEN_MINUTES, THIRTY_MINUTES, FORTY_FIVE_MINUTES,
	ONE_HOUR, TWO_HOURS, FOUR_HOURS, EIGHT_HOURS, TWELVE_HOURS
}

/** The member names of {@link ExpiresAfter} (`"ONE_DAY"`, …), the shape the api serializes on the wire. */
export type ExpiresAfterName = keyof typeof ExpiresAfter;

/**
 * The wire NAME for a retention. The api serializes and binds `ExpireAfter` as its Java enum name, so the
 * app's numeric enum is mapped to that name at the HTTP boundary — every request and response speaks
 * names: the PATCH body, the GET projection, and since kitecorp/transferify-api-service#690 the three
 * create bodies too (`transferApi`'s `toWireCreate`).
 */
export function expiresAfterName(value: ExpiresAfter): ExpiresAfterName {
	return ExpiresAfter[value] as ExpiresAfterName;
}

/**
 * The numeric {@link ExpiresAfter} for a wire NAME, or null for an absent/unknown value. Lets the
 * rest of the app work in the single numeric enum the create flow and {@link ../home/gating} already
 * use, instead of leaking the wire string representation past the API layer.
 */
export function expiresAfterFromName(name: string | null | undefined): ExpiresAfter | null {
	if (name == null) {
		return null;
	}
	const value = (ExpiresAfter as Record<string, number | string>)[name];
	return typeof value === "number" ? value : null;
}

export default ExpiresAfter;